import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { adminGet } from './adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DeviceTypeManager } from './DeviceTypeManager';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

const QC_BADGE = {
  passed: { bg: '#E7F2EE', fg: '#0B6B55', label: 'Passed' },
  failed: { bg: '#FBEBEB', fg: '#8C2B2B', label: 'Failed · RMA' },
  pending: { bg: '#EFEDE6', fg: '#6b7069', label: 'Pending' },
  not_bound: { bg: '#F4F3EF', fg: '#9a9e96', label: 'Not bound' },
};

export default function AdminDevicesPage() {
  const [devices, setDevices] = useState([]);
  const [types, setTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [all, setAll] = useState([]);   // unfiltered, for the per-type stage counts on the chips
  const navigate = useNavigate();
  const { role } = useOutletContext() || {};
  const canReceive = !role || role === 'platform_admin' || role === 'assembly_tech';
  const canImport = !role || role === 'platform_admin';
  const [showTypes, setShowTypes] = useState(false);

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (typeFilter) qs.set('device_type', typeFilter);
    if (statusFilter) qs.set('lifecycle_status', statusFilter);
    adminGet(`/devices?${qs}`).then(setDevices).catch(e => toast.error(e.message));
    adminGet('/devices').then(setAll).catch(() => {});
  }, [typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminGet('/device-types').then(setTypes).catch(() => {}); }, []);
  useEffect(() => { if (new URLSearchParams(window.location.search).get('register') === '1') navigate('/admin/devices/receive', { replace: true }); }, [navigate]);

  // stage counts per type: { HUB: { total, IN_STOCK, ACTIVE, ... } }
  const counts = useMemo(() => {
    const c = {};
    all.forEach(d => {
      const k = d.device_type || d.admin_device_type || '?';
      c[k] = c[k] || { total: 0 };
      c[k].total += 1;
      c[k][d.lifecycle_status] = (c[k][d.lifecycle_status] || 0) + 1;
    });
    return c;
  }, [all]);
  const stageLine = (code) => {
    const c = counts[code]; if (!c) return '0';
    const parts = Object.entries(c).filter(([k]) => k !== 'total').sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${v} ${k.toLowerCase().replace('_', ' ')}`);
    return `${c.total} · ${parts.join(' · ')}`;
  };

  return (
    <div className="flex" data-testid="admin-devices-page">
      {showTypes && <DeviceTypeManager types={types} onChanged={setTypes} onClose={() => setShowTypes(false)} />}
      <div className="flex-1 p-8 min-w-0">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Inventory</h1>
          <div className="flex items-center gap-3">
            {canImport && (
              <Button variant="outline" data-testid="manage-types-btn" onClick={() => setShowTypes(true)}
                className="bg-white border-[#D9D6CD]">Manage types</Button>
            )}
            {canReceive && (
              <Button data-testid="goto-receive-btn" onClick={() => navigate('/admin/devices/receive')}
                className="text-white hover:opacity-90" style={{ background: '#0B6B55' }}>Receive devices</Button>
            )}
            <span className="text-sm" style={{ color: '#9a9e96' }}>{devices.length} devices</span>
          </div>
        </div>
        <div className="flex gap-2 mb-5 flex-wrap">
          {['', ...types.map(t => t.code)].map(code => (
            <button key={code || 'all'} data-testid={`type-filter-${code || 'all'}`} onClick={() => setTypeFilter(code)}
              className="px-4 py-1.5 rounded-full text-sm border transition-colors text-left"
              style={typeFilter === code ? { background: '#E7F2EE', borderColor: '#0B6B55', color: '#0B6B55' } : { background: 'white', borderColor: '#D9D6CD', color: '#4b504a' }}>
              <span className="font-medium">{code ? (types.find(t => t.code === code)?.display_name || code) : 'All types'}</span>
              <span className="block text-[10px] leading-tight" style={{ color: '#9a9e96' }} data-testid={`type-count-${code || 'all'}`}>{code ? stageLine(code) : `${all.length} devices`}</span>
            </button>
          ))}
          <select data-testid="status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-full text-sm border bg-white" style={{ borderColor: '#D9D6CD', color: '#4b504a' }}>
            <option value="">Status: any</option>
            {['UNASSIGNED', 'IN_STOCK', 'PROVISIONED', 'ACTIVE', 'DELIVERED', 'RETURNED', 'FAULTY', 'RETIRED'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="rounded-lg border bg-white overflow-x-auto" style={{ borderColor: '#E5E3DC' }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
                <th className="py-3 px-4 font-medium">Track0tic ID</th><th className="font-medium">Type</th>
                <th className="font-medium">Modem IMEI</th><th className="font-medium">M2M MSISDN</th>
                <th className="font-medium">Firmware</th><th className="font-medium">QC</th><th className="font-medium pr-4">Holder</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(d => {
                const badge = QC_BADGE[d.qc_status] || QC_BADGE.pending;
                return (
                  <tr key={d.id} data-testid={`device-row-${d.id}`} className="border-b hover:bg-[#FAF9F6] cursor-pointer" style={{ borderColor: '#F3F1EA' }}
                    onClick={() => navigate(`/admin/devices/${d.id}`)}>
                    <td className="py-3 px-4" style={mono}>
                      {d.trackotic_id ? <span className="underline decoration-dotted" style={{ color: '#0B6B55' }}>{d.trackotic_id}</span> : '—'}
                    </td>
                    <td>{d.device_type_display}</td>
                    <td style={mono}>{d.imei}</td>
                    <td style={mono}>{d.msisdn || <span style={{ color: '#9a9e96' }}>Not bound</span>}</td>
                    <td style={mono}>{d.firmware || '—'}</td>
                    <td><span className="px-2 py-0.5 rounded text-xs" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span></td>
                    <td className="pr-4">{d.holder}</td>
                  </tr>
                );
              })}
              {devices.length === 0 && <tr><td colSpan={7} className="py-8 text-center" style={{ color: '#9a9e96' }}>No devices</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
